
"use client"

import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { cn } from "@/lib/utils"
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar"
import { useRealPhoto } from "@/hooks/use-real-photo"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
  // A generated initials picture or Google's default letter avatar is not a
  // photo. Keep the Image mounted with no src: Radix then reports "error" and
  // the BB vector fallback shows (unmounting would leave it stuck on "loaded").
  const photo = useRealPhoto(typeof src === "string" ? src : undefined)
  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={photo || undefined}
      className={cn("aspect-square h-full w-full outline-none", className)}
      {...props}
    />
  )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

/**
 * No uploaded photo → the animated, music-reactive vector artwork with the
 * person's name (or the initials the caller passes) instead of bare initials
 * (Gordon 2026-10-31: "Users that dont upload thumbnails should … generate
 * auto text and animated vector thumbnails that react on the music"). Pass
 * `name` for the full name; icons and other non-text children render as-is.
 */
const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & { name?: string | null }
>(({ className, children, name, ...props }, ref) => {
  const label = (name || (typeof children === "string" ? children : "")).trim()
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "relative flex h-full w-full items-center justify-center overflow-hidden bg-muted",
        className
      )}
      {...props}
    >
      {label && label !== "?" ? (
        <GenerativeArtistAvatar name={label} className="absolute inset-0 h-full w-full" animate />
      ) : children}
    </AvatarPrimitive.Fallback>
  )
})
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

/**
 * Generate initials from a name string (e.g., "John Doe" → "JD", "Madonna" → "MA")
 */
function getInitials(name?: string | null): string {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

export { Avatar, AvatarImage, AvatarFallback, getInitials }
